import { useEffect, useId, useMemo, useRef, useState } from "react";

export function cn(...values) {
  return values.filter(Boolean).join(" ");
}

export function Logo({ compact = false }) {
  return (
    <div className="flex items-center gap-2.5 font-bold tracking-[-0.03em] text-foreground" aria-label="RevPal">
      <span className="grid size-[28px] place-items-center rounded-[9px] bg-primary shadow-[0_2px_6px_oklch(0.48_0.19_260_/_0.35)]" aria-hidden="true">
        <i className="size-[11px] rounded-[3px] border-[2.5px] border-white" />
      </span>
      {!compact && <span>RevPal</span>}
    </div>
  );
}

export function Eyebrow({ children }) {
  return <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{children}</p>;
}

export function Button({ variant = "secondary", className, type = "button", ...props }) {
  const variants = {
    primary: "border-transparent bg-primary text-white shadow-[0_1px_2px_oklch(0_0_0_/_0.08)] hover:bg-primary-600",
    secondary: "border-border bg-card text-foreground shadow-[0_1px_2px_oklch(0_0_0_/_0.04)] hover:bg-muted",
    ghost: "border-transparent bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
    danger: "border-transparent bg-destructive/10 text-destructive hover:bg-destructive/15",
  };
  return (
    <button
      type={type}
      className={cn(
        "inline-flex min-h-8 cursor-pointer items-center justify-center rounded-lg border px-3.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-55",
        variants[variant],
        className,
      )}
      {...props}
    />
  );
}

export function IconButton({ label, className, ...props }) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn("grid size-8 cursor-pointer place-items-center rounded-lg bg-transparent text-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary", className)}
      {...props}
    />
  );
}

const fieldInputClasses = "min-h-[38px] w-full rounded-xl border border-border bg-card px-3.5 text-sm text-foreground shadow-[0_1px_2px_oklch(0_0_0_/_0.04)] outline-none transition-shadow hover:border-neutral-300 focus:border-primary/40 focus:ring-2 focus:ring-primary/15";

export function Field({ label, hint, className, ...props }) {
  return (
    <label className="grid gap-1.5 text-[13px] font-medium text-foreground">
      <span>{label} {hint && <small className="font-normal text-muted-foreground">{hint}</small>}</span>
      <input className={cn(fieldInputClasses, className)} {...props} />
    </label>
  );
}

export function SelectField({ label, hint, children, className, ...props }) {
  return (
    <label className="grid gap-1.5 text-[13px] font-medium text-foreground">
      <span>{label} {hint && <small className="font-normal text-muted-foreground">{hint}</small>}</span>
      <select className={cn(fieldInputClasses, className)} {...props}>{children}</select>
    </label>
  );
}

export function SearchCombobox({
  label,
  hint,
  value = "",
  options = [],
  onChange,
  onSearchChange,
  placeholder = "Type to search…",
  emptyMessage = "No matching records",
  loading = false,
  disabled = false,
  required = false,
  name,
  className,
  autoFocus = false,
}) {
  const generatedId = useId();
  const inputId = `combobox-${generatedId}`;
  const listboxId = `${inputId}-listbox`;
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const normalizedOptions = useMemo(() => {
    const unique = new Map();
    for (const option of options) {
      const optionValue = String(option.value);
      if (!unique.has(optionValue)) unique.set(optionValue, { ...option, value: optionValue });
    }
    return [...unique.values()];
  }, [options]);
  const selected = normalizedOptions.find((option) => option.value === String(value)) || null;
  const filteredOptions = useMemo(() => {
    const search = query.trim().toLocaleLowerCase();
    if (!search) return normalizedOptions;
    return normalizedOptions.filter((option) => (
      [option.label, option.description, option.keywords]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase()
        .includes(search)
    ));
  }, [normalizedOptions, query]);

  useEffect(() => {
    if (!open) setQuery(selected?.label || "");
  }, [open, selected?.label]);

  useEffect(() => {
    setActiveIndex((current) => Math.max(0, Math.min(current, Math.max(0, filteredOptions.length - 1))));
  }, [filteredOptions.length]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (rootRef.current?.contains(event.target)) return;
      setOpen(false);
      setQuery(selected?.label || "");
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [selected?.label]);

  function showOptions() {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setActiveIndex(0);
    onSearchChange?.("");
  }

  function choose(option) {
    onChange(option.value);
    setQuery(option.label);
    setOpen(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) showOptions();
      else if (filteredOptions.length) setActiveIndex((current) => Math.min(current + 1, filteredOptions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) showOptions();
      else setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      if (filteredOptions[activeIndex]) choose(filteredOptions[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setQuery(selected?.label || "");
    } else if (event.key === "Tab") {
      setOpen(false);
      setQuery(selected?.label || "");
    }
  }

  return (
    <div className="grid gap-1.5 text-[13px] font-medium text-foreground" ref={rootRef}>
      <label htmlFor={inputId}>{label} {hint && <small className="font-normal text-muted-foreground">{hint}</small>}</label>
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          name={name || `${inputId}-search`}
          role="combobox"
          type="text"
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-activedescendant={open && filteredOptions[activeIndex] ? `${inputId}-option-${activeIndex}` : undefined}
          aria-required={required}
          autoFocus={autoFocus}
          required={required}
          disabled={disabled}
          value={query}
          placeholder={disabled ? "Select a company first" : placeholder}
          className={cn(
            fieldInputClasses,
            "pr-20 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400 max-sm:text-base",
            className,
          )}
          onFocus={() => { if (!open) showOptions(); }}
          onChange={(event) => {
            const nextQuery = event.target.value;
            setQuery(nextQuery);
            setOpen(true);
            setActiveIndex(0);
            if (value) onChange("");
            onSearchChange?.(nextQuery);
          }}
          onKeyDown={handleKeyDown}
        />
        {(value || query) && !disabled && (
          <button
            type="button"
            aria-label={`Clear ${label.toLocaleLowerCase()}`}
            className="absolute right-9 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-base font-normal text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { onChange(""); setQuery(""); setOpen(true); setActiveIndex(0); onSearchChange?.(""); inputRef.current?.focus(); }}
          >×</button>
        )}
        <button
          type="button"
          aria-label={open ? `Close ${label.toLocaleLowerCase()} options` : `Open ${label.toLocaleLowerCase()} options`}
          className="absolute right-1 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-sm font-normal text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-55"
          disabled={disabled}
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (open) { setOpen(false); setQuery(selected?.label || ""); }
            else { showOptions(); inputRef.current?.focus(); }
          }}
        >{open ? "▴" : "▾"}</button>
        {open && !disabled && (
          <div id={listboxId} role="listbox" className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-border bg-card p-1 shadow-elevated">
            {loading ? <p className="px-3 py-3 text-xs font-normal text-muted-foreground">Searching…</p> : filteredOptions.length ? filteredOptions.map((option, index) => (
              <button
                id={`${inputId}-option-${index}`}
                role="option"
                aria-selected={option.value === String(value)}
                type="button"
                key={option.value}
                className={cn(
                  "grid w-full gap-0.5 rounded-md px-3 py-2 text-left focus-visible:outline-none",
                  index === activeIndex ? "bg-accent text-foreground" : "text-neutral-600 hover:bg-muted",
                )}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(option)}
              >
                <span className="truncate text-[13px] font-medium">{option.label}</span>
                {option.description && <span className="truncate text-[11px] font-normal text-muted-foreground">{option.description}</span>}
              </button>
            )) : <p className="px-3 py-3 text-xs font-normal text-muted-foreground">{emptyMessage}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export function TextAreaField({ label, hint, className, ...props }) {
  return (
    <label className="grid gap-1.5 text-[13px] font-medium text-foreground">
      <span>{label} {hint && <small className="font-normal text-muted-foreground">{hint}</small>}</span>
      <textarea
        className={cn("min-h-[110px] w-full resize-y rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm text-foreground shadow-[0_1px_2px_oklch(0_0_0_/_0.04)] outline-none transition-shadow hover:border-neutral-300 focus:border-primary/40 focus:ring-2 focus:ring-primary/15", className)}
        {...props}
      />
    </label>
  );
}

export function InlineInput({ className, ...props }) {
  return <input className={cn("min-h-9 w-full min-w-[110px] rounded-md border border-transparent bg-transparent px-2 text-xs text-foreground outline-none hover:border-border hover:bg-card focus:border-primary/40 focus:bg-card focus:ring-2 focus:ring-primary/15", className)} {...props} />;
}

export function InlineSelect({ className, children, ...props }) {
  return <select className={cn("min-h-9 w-full min-w-[110px] rounded-md border border-transparent bg-transparent px-2 text-xs text-foreground outline-none hover:border-border hover:bg-card focus:border-primary/40 focus:bg-card focus:ring-2 focus:ring-primary/15", className)} {...props}>{children}</select>;
}

export function InlineTextArea({ className, ...props }) {
  return <textarea className={cn("min-h-16 w-full min-w-[150px] resize-y rounded-md border border-transparent bg-transparent px-2 py-2 text-xs text-foreground outline-none hover:border-border hover:bg-card focus:border-primary/40 focus:bg-card focus:ring-2 focus:ring-primary/15", className)} {...props} />;
}

export function Notice({ children, tone = "error" }) {
  const tones = {
    error: "border-destructive/25 bg-destructive/8 text-destructive",
    info: "border-border bg-muted text-neutral-600",
  };
  return <div className={cn("rounded-xl border px-4 py-3 text-[13px] leading-6", tones[tone])} role={tone === "error" ? "alert" : undefined}>{children}</div>;
}

export function Badge({ children, status, className }) {
  return (
    <span className={cn(
      "inline-flex min-h-5 items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[11px] font-medium",
      status === "active" && "bg-green-500/12 text-green-700",
      status === "succeeded" && "bg-green-500/12 text-green-700",
      status === "failed" && "bg-destructive/10 text-destructive",
      status === "running" && "bg-primary/10 text-primary-600",
      status === "replayed" && "bg-blue-500/12 text-blue-700",
      status === "no_result" && "bg-amber-500/15 text-amber-700",
      status === "skipped" && "bg-muted text-muted-foreground",
      status === "candidate" && "bg-amber-500/15 text-amber-700",
      status === "revoked" && "bg-muted text-muted-foreground",
      status === "stale" && "bg-orange-500/12 text-orange-700",
      status === "superseded" && "bg-muted text-muted-foreground line-through",
      !status && "border border-border text-muted-foreground",
      className,
    )}>{children}</span>
  );
}

export function TabList({ label, children, className }) {
  return <div className={cn("inline-flex max-w-full gap-0.5 overflow-x-auto rounded-lg bg-muted p-[3px]", className)} role="tablist" aria-label={label}>{children}</div>;
}

export function TabButton({ selected, children, className, ...props }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      className={cn(
        "shrink-0 cursor-pointer rounded-[7px] px-3.5 py-1.5 text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary",
        selected ? "bg-card font-semibold text-foreground shadow-[0_1px_2px_oklch(0_0_0_/_0.06)]" : "font-medium text-muted-foreground hover:text-foreground",
        className,
      )}
      {...props}
    >{children}</button>
  );
}

const avatarPalette = ["#ef4444", "#f97316", "#f59e0b", "#84cc16", "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#d946ef", "#ec4899", "#f43f5e"];

export function Avatar({ name }) {
  const initials = name.split(/\s+/u).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) % avatarPalette.length;
  return (
    <span
      className="grid size-8 shrink-0 place-items-center rounded-full text-[12px] font-semibold text-white"
      style={{ background: avatarPalette[hash] }}
      aria-hidden="true"
    >{initials}</span>
  );
}

export function PageHeader({ eyebrow, title, description, action }) {
  return (
    <header className="grid gap-5 sm:flex sm:items-end sm:justify-between">
      <div className="grid gap-2">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="text-[30px] font-bold leading-[1.1] tracking-[-0.02em]">{title}</h1>
        {description && <p className="max-w-[64ch] text-sm leading-6 text-muted-foreground">{description}</p>}
      </div>
      {action}
    </header>
  );
}

export function Panel({ children, className }) {
  return <section className={cn("rounded-xl bg-card p-6 shadow-soft", className)}>{children}</section>;
}

export function RecordWorkspaceLayout({ children, activity }) {
  return (
    <div className="grid items-start gap-8 xl:grid-cols-[minmax(0,1.15fr)_minmax(380px,.85fr)]">
      <div className="grid min-w-0 content-start gap-6">{children}</div>
      <aside className="min-w-0 xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)] xl:overflow-y-auto xl:border-l xl:border-border xl:pl-6" aria-label="Record activity">
        {activity}
      </aside>
    </div>
  );
}

export function CardSection({ title, meta, action, children, className }) {
  return (
    <section className={cn("rounded-xl bg-card shadow-soft", className)}>
      <header className="flex min-h-[60px] flex-wrap items-center justify-between gap-3 px-5 pb-2 pt-4">
        <div className="grid gap-0.5">
          <h2 className="text-base font-semibold tracking-[-0.01em]">{title}</h2>
          {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
        </div>
        {action}
      </header>
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}

export function SectionHeading({ title, meta, action }) {
  return (
    <div className="flex min-h-[74px] items-center justify-between gap-4">
      <div className="grid gap-1"><h2 className="text-xl font-semibold tracking-[-0.01em]">{title}</h2>{meta && <p className="text-xs text-muted-foreground">{meta}</p>}</div>
      {action}
    </div>
  );
}

export function EmptyState({ title, description }) {
  return (
    <div className="grid min-h-[280px] place-content-center justify-items-center gap-1 text-center">
      <span className="mb-3 grid size-[46px] place-items-center rounded-[13px] bg-muted text-xl text-neutral-400" aria-hidden="true">⌁</span>
      <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
      <p className="max-w-[48ch] text-[13px] leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

export function LoadingRows({ count = 3 }) {
  return (
    <div className="grid gap-1.5 py-1.5">
      {Array.from({ length: count }, (_, index) => (
        <i
          className="block h-[52px] animate-shimmer rounded-lg bg-[linear-gradient(90deg,oklch(0.955_0.005_250)_25%,oklch(0.94_0.012_260)_50%,oklch(0.955_0.005_250)_75%)] bg-[length:200%_100%]"
          key={index}
        />
      ))}
    </div>
  );
}

export function AttributionBadge({ actor }) {
  const icon = actor?.type === "human" ? "●" : actor?.type === "agent" ? "◇" : actor?.type === "provider" ? "⌕" : "↓";
  return <Badge><span aria-hidden="true">{icon}</span>&nbsp;{actor?.name || actor?.type || "unknown"}</Badge>;
}

export function Table({ children }) {
  return <div className="overflow-x-auto"><table className="w-full border-collapse text-[13px]">{children}</table></div>;
}

export function TableHead({ children }) {
  return <thead><tr className="h-10 bg-muted/50 text-left text-[11px] uppercase tracking-[.06em] text-muted-foreground">{children}</tr></thead>;
}

export function TableHeader({ children, className }) {
  return <th className={cn("border-b border-border border-r border-r-neutral-100 px-3 font-semibold last:border-r-0", className)}>{children}</th>;
}

export function TableRow({ children, className, ...props }) {
  return <tr className={cn("border-b border-neutral-100 transition-colors last:border-b-0 hover:bg-muted/50", className)} {...props}>{children}</tr>;
}

export function TableCell({ children, className }) {
  return <td className={cn("border-r border-neutral-100 px-3 py-2.5 align-middle last:border-r-0", className)}>{children}</td>;
}
